// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — three ways to play, on three different axes (principle #14). The draft is identical in
// all three; what changes is WHAT decides where a tile lands, WHERE you may put it, and WHEN it goes
// there.
//
//   Setwork    the destination is fixed by the tile. The Mosaic is printed, so a full course has
//              exactly one legal cell and you can compute everybody's future. Denial is exact.
//   Freehand   WHERE. The Mosaic is blank and you NOMINATE a course's column when you first load it
//              — committing to a cell long before you know what the Mosaic will look like when the
//              tile lands.
//   Quickfire  WHEN. A course lays the instant it fills, mid-turn, and the game ENDS the moment a
//              Mosaic row completes — mid-round, with everyone's unlaid courses still on the bench.
//
// Quickfire's first draft was "lays instantly, then locked until the round ends", and an adversarial
// pass proved that was not a mode at all: the row restriction cannot tighten mid-round because the
// course that would tighten it is the one that just locked, Spoil overflow already lands immediately
// in every mode, and a live scoreboard is a rendering choice. What was left was Setwork plus a 1-3
// point lay-ordering wrinkle. The INSTANT END TRIGGER is what makes WHEN an axis: the last round
// becomes a race, seat order in that round is suddenly worth real points, and a rival can end the
// game while your 4/5 course is still waiting for one tile.
//
// The host's pick travels FROZEN inside the round-start payload and a guest renders state().hostOpts
// — a setting each peer reads from its own UI is a setting two peers can disagree about, and here it
// would mean two different rulebooks on the same seed. An id off the wire is always modeOf()-checked
// with Object.hasOwn, so a wire key can never resolve a prototype member and hand the generator a
// Mode of undefined fields.

export type ModeId = 'setwork' | 'freehand' | 'quickfire';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Is the Mosaic printed (destination forced) or blank (column nominated at load)? */
  readonly printed: boolean;
  /** Does a course lay the instant it fills, rather than at round end? */
  readonly instantLay: boolean;
  /** Does completing a Mosaic row end the game AT ONCE, rather than at the end of that round? */
  readonly instantEnd: boolean;
  /** A backstop so no rulebook bug can hang a lockstep room. Must never fire — asserted. */
  readonly roundCap: number;
}

export const MODES: Record<ModeId, Mode> = {
  setwork: {
    id: 'setwork',
    name: 'Setwork',
    blurb: 'The Mosaic is printed: every kind has one cell in each row, so you can read exactly what your rivals are building — and take it.',
    printed: true,
    instantLay: false,
    instantEnd: false,
    roundCap: 15,
  },
  freehand: {
    id: 'freehand',
    name: 'Freehand',
    blurb: 'The Mosaic is blank. Name a course’s column the first time you load it, long before you know what the wall will look like when the tile lands.',
    printed: false,
    instantLay: false,
    instantEnd: false,
    roundCap: 15,
  },
  quickfire: {
    id: 'quickfire',
    name: 'Quickfire',
    blurb: 'Courses lay the instant they fill — and the game ends the moment somebody completes a row, with everyone else’s courses still on the bench.',
    printed: true,
    instantLay: true,
    instantEnd: true,
    roundCap: 15,
  },
};

export const MODE_IDS: readonly ModeId[] = ['setwork', 'freehand', 'quickfire'];
export const DEFAULT_MODE: ModeId = 'setwork';

/** Validate an id that may have come off the wire or out of storage. Never returns undefined. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id as ModeId];
  return MODES[DEFAULT_MODE];
}
